import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, CalendarOff, Phone, Mail, Euro, X, Calendar as CalIcon } from "lucide-react";
import { format, isSameDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import { nl, fr, es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WeatherStrip, { WeatherPill } from "@/components/WeatherStrip";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DF_LOCALES = { nl, fr, es, en: enUS };

const fmtDate = (d) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * Reusable agenda page used by:
 *   - admin     (full power: see/edit/drag/unschedule everything, see unscheduled list)
 *   - marketing_manager (see/edit/drag scheduled+unscheduled of managed agents)
 *   - agent     (read-only view of own scheduled shoots)
 *
 * The render mode is derived from the current user's role.
 */
export default function AgendaPage() {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const dfLocale = DF_LOCALES[(i18n.language || "nl").slice(0, 2)] || nl;
    const WEEKDAYS = t("agenda.weekdays", { returnObjects: true });
    const navigate = useNavigate();
    const role = user?.role;
    const accountType = user?.account_type;
    const isAdmin = role === "admin";
    const isMM = accountType === "marketing_manager";
    const canEdit = isAdmin || isMM;

    const [month, setMonth] = useState(new Date());
    const [data, setData] = useState({ blocked_dates: [], scheduled: [], unscheduled: [] });
    const [loading, setLoading] = useState(true);
    const [dragOverDate, setDragOverDate] = useState(null);
    const [popup, setPopup] = useState(null);
    const [scheduleTime, setScheduleTime] = useState("10:00");
    const [products, setProducts] = useState([]);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelData, setCancelData] = useState({ reason: "", slots: [{ date: "", time: "10:00" }, { date: "", time: "10:00" }, { date: "", time: "10:00" }] });
    const [cancelling, setCancelling] = useState(false);
    const [weather, setWeather] = useState(null); // {days: [...], lat, lon}
    const [weatherFor, setWeatherFor] = useState(null); // {city, address, label} | null

    // Unique gemeenten (cities) from BOTH unscheduled and scheduled projects.
    // Admins/MMs typically use unscheduled to plan; agents only see their scheduled shoots.
    const availableCities = useMemo(() => {
        const map = new Map(); // city → {city, address, count}
        const sources = [...(data.unscheduled || []), ...(data.scheduled || [])];
        for (const u of sources) {
            const city = (u.address_city || "").trim();
            if (!city) continue;
            const existing = map.get(city);
            if (existing) {
                existing.count += 1;
            } else {
                const street = (u.address_street || "").trim();
                const zip = (u.address_zip || "").trim();
                const fullAddr = [street, [zip, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                map.set(city, { city, address: fullAddr || city, count: 1 });
            }
        }
        return Array.from(map.values()).sort((a, b) => a.city.localeCompare(b.city, "nl"));
    }, [data.unscheduled, data.scheduled]);

    // Auto-select the first available city when the list loads
    useEffect(() => {
        if (!weatherFor && availableCities.length > 0) {
            const first = availableCities[0];
            setWeatherFor({ city: first.city, address: first.address, label: first.city });
        }
        // If the currently selected city is no longer in the list, reset to the first one
        if (weatherFor && !availableCities.some((c) => c.city === weatherFor.city)) {
            setWeatherFor(availableCities.length > 0 ? { city: availableCities[0].city, address: availableCities[0].address, label: availableCities[0].city } : null);
        }
    }, [availableCities, weatherFor]);

    // Build a date → weather lookup for tiny pills inside appointment cards
    const weatherByDate = useMemo(() => {
        if (!weather?.days) return {};
        const m = {};
        for (const d of weather.days) m[d.date] = d;
        return m;
    }, [weather]);

    const load = async () => {
        try {
            const r = await api.get("/availability/agenda");
            setData({ blocked_dates: r.data.blocked_dates || [], scheduled: r.data.scheduled || [], unscheduled: r.data.unscheduled || [] });
        } catch {
            toast.error(t("agenda.load_failed"));
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        api.get("/products", { params: { include_hidden: true } }).then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    // Fetch weather for all roles — agents also benefit from seeing the forecast for their shoot locations
    useEffect(() => {
        if (!weatherFor?.address) { setWeather(null); return; }
        let mounted = true;
        const params = `?address=${encodeURIComponent(weatherFor.address)}&days=14`;
        api.get(`/weather/forecast${params}`)
            .then((r) => mounted && setWeather(r.data))
            .catch(() => mounted && setWeather(null));
        return () => { mounted = false; };
    }, [weatherFor]);

    const blockedSet = new Set(data.blocked_dates.map((b) => b.date));
    const scheduledByDate = useMemo(() => data.scheduled.reduce((acc, s) => {
        (acc[s.date] = acc[s.date] || []).push(s);
        return acc;
    }, {}), [data.scheduled]);

    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });

    const reschedule = async (reqId, scheduled_date, scheduled_time) => {
        try {
            await api.patch(`/requests/${reqId}/schedule`, { scheduled_date, scheduled_time });
            return true;
        } catch (err) {
            const detail = err.response?.data?.detail || "";
            // Detect the MM-approval block — offer the admin to override on the spot
            if (detail.toLowerCase().includes("marketing manager")) {
                const yes = window.confirm(
                    "Deze aanvraag heeft nog geen goedkeuring van de marketing manager.\n\n" +
                    "Wil je de MM-goedkeuring overslaan en het project tóch plannen? " +
                    "Dit wordt geregistreerd in het logboek."
                );
                if (yes) {
                    try {
                        await api.patch(`/requests/${reqId}/schedule`, {
                            scheduled_date, scheduled_time, override_mm_approval: true,
                        });
                        toast.info("MM-goedkeuring overgeslagen");
                        return true;
                    } catch (err2) {
                        toast.error(err2.response?.data?.detail || t("agenda.failed"));
                        return false;
                    }
                }
            }
            toast.error(detail || t("agenda.failed"));
            return false;
        }
    };

    const handleDrop = async (e, date) => {
        e.preventDefault();
        setDragOverDate(null);
        if (!canEdit) return;
        const reqId = e.dataTransfer.getData("text/plain");
        if (!reqId) return;
        const targetDate = format(date, "yyyy-MM-dd");
        const existing = data.scheduled.find((s) => s.request_id === reqId);
        const tm = existing?.time || "10:00";
        const ok = await reschedule(reqId, targetDate, tm);
        if (ok) {
            toast.success(t("agenda.scheduled_on", { date: fmtDate(targetDate) }));
            load();
        }
    };

    const scheduleFromPopup = async () => {
        if (!popup || !canEdit) return;
        const dateToUse = popup._scheduleDate || popup.preferred_date || popup.preferred_slots?.[0]?.date;
        if (!dateToUse) {
            toast.error(t("agenda.no_date"));
            return;
        }
        const ok = await reschedule(popup.request_id, dateToUse, scheduleTime);
        if (ok) {
            toast.success(t("agenda.scheduled_toast"));
            setPopup(null);
            load();
        }
    };

    const projectLink = (reqId) => isAdmin ? `/admin/projects/${reqId}` : `/projects/${reqId}`;

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="agenda-page">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-10">
                <Link to={isAdmin ? "/admin" : "/dashboard"} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="agenda-back">
                    <ArrowLeft className="h-4 w-4" /> {t("agenda.back")}
                </Link>

                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">
                            {isAdmin ? t("agenda.eyebrow_admin") : isMM ? t("agenda.eyebrow_mm") : t("agenda.eyebrow_agent")}
                        </div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                            {isAdmin ? t("agenda.title_admin") : isMM ? t("agenda.title_mm") : t("agenda.title_agent")}
                        </h1>
                        {isMM && <div className="text-sm text-zinc-500 mt-2">{t("agenda.sub_mm")}</div>}
                        {!canEdit && <div className="text-sm text-zinc-500 mt-2">{t("agenda.sub_agent")}</div>}
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-[#E5A93D]" /> {t("agenda.legend_scheduled")}</div>
                        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-red-500" /> {t("agenda.legend_blocked")}</div>
                        {canEdit && <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-[#7C3AED]" /> {t("agenda.legend_pref")}</div>}
                    </div>
                </div>

                <div className={`grid grid-cols-1 ${canEdit ? "lg:grid-cols-[1fr_320px]" : ""} gap-6`}>
                    {/* Calendar */}
                    <div>
                        {/* Weather strip — visible for all roles */}
                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-4 lg:p-5 mb-6" data-testid="weather-section">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{t("agenda.weather_title")}</div>
                                    <div className="text-sm text-zinc-600 mt-1">
                                        {weatherFor?.label ? (
                                            <>{t("agenda.weather_city")} <span className="text-zinc-950 font-medium">{weatherFor.label}</span></>
                                        ) : (
                                            <span className="text-zinc-500">{t("agenda.weather_pick")}</span>
                                        )}
                                        <span className="text-zinc-500 text-xs ml-2">{t("agenda.weather_src")}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Select
                                        value={weatherFor?.city || ""}
                                        onValueChange={(city) => {
                                            const match = availableCities.find((c) => c.city === city);
                                            if (match) setWeatherFor({ city: match.city, address: match.address, label: match.city });
                                        }}
                                        disabled={availableCities.length === 0}
                                    >
                                        <SelectTrigger className="h-9 w-[260px] bg-[#FFFFFF] border-zinc-200 text-sm rounded-sm focus:ring-[#E5A93D]" data-testid="weather-city-select">
                                            <SelectValue placeholder={availableCities.length === 0 ? t("agenda.no_props") : t("agenda.pick_city")} />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#FFFFFF] border-zinc-200 text-zinc-950">
                                            {availableCities.map((c) => (
                                                <SelectItem key={c.city} value={c.city} className="focus:bg-[#E5A93D]/15 focus:text-zinc-950" data-testid={`weather-city-option-${c.city}`}>
                                                    {c.count > 1
                                                        ? `${c.city} · ${c.count} ${canEdit ? t("agenda.count_requests") : t("agenda.count_props")}`
                                                        : c.city}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {weather?.days?.length > 0 ? (
                                <WeatherStrip days={weather.days} />
                            ) : (
                                <div className="text-xs text-zinc-500 text-center py-6" data-testid="weather-empty">
                                    {availableCities.length === 0
                                        ? (canEdit ? t("agenda.weather_none_edit") : t("agenda.weather_none_agent"))
                                        : t("agenda.weather_loading")}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between mb-4">
                            <button onClick={() => setMonth(subMonths(month, 1))} className="px-3 py-1.5 text-sm bg-[#FFFFFF] border border-zinc-200 hover:border-zinc-400 rounded-sm" data-testid="cal-prev">{t("agenda.prev")}</button>
                            <h2 className="font-display font-bold text-2xl capitalize" data-testid="cal-month">{format(month, "MMMM yyyy", { locale: dfLocale })}</h2>
                            <button onClick={() => setMonth(addMonths(month, 1))} className="px-3 py-1.5 text-sm bg-[#FFFFFF] border border-zinc-200 hover:border-zinc-400 rounded-sm" data-testid="cal-next">{t("agenda.next")}</button>
                        </div>

                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-4 lg:p-6">
                            <div className="grid grid-cols-7 gap-2 mb-2">
                                {WEEKDAYS.map((d) => <div key={d} className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center py-2">{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-2">
                                {days.map((d) => {
                                    const key = format(d, "yyyy-MM-dd");
                                    const isBlocked = blockedSet.has(key);
                                    const shoots = scheduledByDate[key] || [];
                                    const isCurrentMonth = d.getMonth() === month.getMonth();
                                    const isToday = isSameDay(d, new Date());
                                    const isDragOver = dragOverDate === key;
                                    return (
                                        <div
                                            key={key}
                                            onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverDate(key); } : undefined}
                                            onDragLeave={canEdit ? () => setDragOverDate(null) : undefined}
                                            onDrop={canEdit ? (e) => handleDrop(e, d) : undefined}
                                            className={`min-h-[100px] p-2 rounded-sm border text-xs transition-colors ${
                                                isDragOver ? "bg-[#E5A93D]/30 border-[#E5A93D] ring-2 ring-[#E5A93D]" :
                                                !isCurrentMonth ? "bg-transparent border-zinc-100 text-zinc-400" :
                                                isBlocked ? "bg-red-500/10 border-red-500/40" :
                                                shoots.length > 0 ? "bg-[#E5A93D]/10 border-[#E5A93D]/40" :
                                                "bg-[#F4F4F5] border-zinc-200"
                                            } ${isToday ? "ring-1 ring-[#E5A93D]" : ""}`}
                                            data-testid={`cal-day-${key}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`font-bold ${isCurrentMonth ? "text-zinc-950" : "text-zinc-400"}`}>{format(d, "d")}</span>
                                                <div className="flex items-center gap-1">
                                                    {isAdmin && weatherByDate[key] && (
                                                        <WeatherPill day={weatherByDate[key]} />
                                                    )}
                                                    {isBlocked && <CalendarOff className="h-3 w-3 text-red-600" />}
                                                </div>
                                            </div>
                                            {shoots.slice(0, 3).map((s) => (
                                                <button
                                                    key={s.request_id}
                                                    draggable={canEdit}
                                                    onDragStart={canEdit ? (e) => e.dataTransfer.setData("text/plain", s.request_id) : undefined}
                                                    onClick={(e) => { e.stopPropagation(); setPopup(s); setScheduleTime(s.time || "10:00"); }}
                                                    className={`w-full text-left mt-1 text-[10px] text-black bg-[#E5A93D]/80 hover:bg-[#E5A93D] rounded-sm px-1.5 py-0.5 truncate ${canEdit ? "cursor-grab active:cursor-grabbing active:bg-[#C8922C]" : ""}`}
                                                    title={`${s.time || ""} · ${s.address}`}
                                                    data-testid={`cal-event-${s.request_id}`}
                                                >
                                                    {s.time && <span className="font-bold">{s.time}</span>} {s.address}
                                                </button>
                                            ))}
                                            {shoots.length > 3 && <div className="text-[10px] text-zinc-500 mt-1">{t("agenda.more", { count: shoots.length - 3 })}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar: unscheduled (only when can edit) */}
                    {canEdit && (
                        <div className="lg:sticky lg:top-24 lg:self-start" data-testid="unscheduled-sidebar">
                            <div className="bg-[#FFFFFF] border border-[#7C3AED]/30 rounded-md p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <CalIcon className="h-4 w-4 text-[#7C3AED]" />
                                    <h3 className="font-display font-bold text-sm tracking-tight">{t("agenda.unscheduled_title")} ({data.unscheduled.length})</h3>
                                </div>
                                <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">{t("agenda.unscheduled_hint")}</p>
                                {data.unscheduled.length === 0 ? (
                                    <div className="text-xs text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">{t("agenda.unscheduled_empty")}</div>
                                ) : (
                                    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                                        {data.unscheduled.map((u) => (
                                            <div
                                                key={u.request_id}
                                                draggable
                                                onDragStart={(e) => e.dataTransfer.setData("text/plain", u.request_id)}
                                                onClick={() => { setPopup(u); setScheduleTime(u.preferred_time || "10:00"); }}
                                                className="cursor-grab active:cursor-grabbing bg-[#F4F4F5] hover:bg-[#F4F4F5] border border-zinc-100 hover:border-[#7C3AED]/50 rounded-sm p-3 transition"
                                                data-testid={`unscheduled-${u.request_id}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <StatusBadge status={u.status} />
                                                    {u.date_pending && <span className="text-[10px] text-zinc-500 italic">{t("agenda.date_tbd")}</span>}
                                                </div>
                                                <div className="text-sm font-medium text-zinc-950 truncate">{u.address}</div>
                                                <div className="text-xs text-[#E5A93D] truncate">{u.agent}</div>
                                                {u.cancel_reason && (
                                                    <div className="mt-1.5 text-[10px] text-red-600 italic" data-testid={`unsched-cancel-reason-${u.request_id}`}>
                                                        {t("agenda.cancelled")} {u.cancel_reason}
                                                    </div>
                                                )}
                                                {(u.preferred_date || u.preferred_slots?.length > 0) && (
                                                    <div className="mt-1.5 text-[10px] text-zinc-500 space-y-0.5">
                                                        {[{ date: u.preferred_date, time: u.preferred_time }, ...(u.preferred_slots || [])].filter((s) => s?.date).slice(0, 3).map((s, i) => (
                                                            <div key={i} className="flex items-center gap-1">
                                                                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[8px] font-bold bg-[#7C3AED]/20 text-[#7C3AED]">{i + 1}</span>
                                                                {fmtDate(s.date)} · {s.time}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {loading && <div className="text-center text-zinc-500 py-8">{t("agenda.loading")}</div>}

                {/* Empty state for read-only agenda */}
                {!loading && !canEdit && data.scheduled.length === 0 && (
                    <div className="mt-10 text-center text-zinc-500 py-16 border border-dashed border-zinc-200 rounded-md">
                        {t("agenda.no_shoots")}
                    </div>
                )}
            </div>

            {/* Popup */}
            {popup && (
                <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPopup(null)} data-testid="cal-popup">
                    <div className="bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <StatusBadge status={popup.status} />
                                <button onClick={() => setPopup(null)} className="text-zinc-500 hover:text-zinc-900"><X className="h-4 w-4" /></button>
                            </div>
                            <h3 className="font-display font-bold text-2xl tracking-tight mb-1">{popup.address_full || popup.address}</h3>
                            <div className="text-sm text-[#E5A93D] mb-4">{popup.agent}</div>

                            <div className="space-y-2 mb-5 text-sm">
                                {popup.phone && <div className="flex items-center gap-2 text-zinc-600"><Phone className="h-3.5 w-3.5 text-zinc-500" /> {popup.phone}</div>}
                                {popup.email && <div className="flex items-center gap-2 text-zinc-600"><Mail className="h-3.5 w-3.5 text-zinc-500" /> {popup.email}</div>}
                                {popup.total_price !== undefined && <div className="flex items-center gap-2 text-zinc-600"><Euro className="h-3.5 w-3.5 text-zinc-500" /> €{popup.total_price}</div>}
                                {popup.publication_date && (() => {
                                    const days = Math.ceil((new Date(popup.publication_date) - new Date()) / (1000 * 60 * 60 * 24));
                                    const urgent = days <= 7;
                                    return (
                                        <div className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm border ${urgent ? "bg-red-500/10 border-red-500/40 text-red-700" : "bg-[#7C3AED]/10 border-[#7C3AED]/30 text-[#7C3AED]"}`} data-testid="popup-pub-date">
                                            <CalIcon className="h-3.5 w-3.5" />
                                            <span className="text-xs">
                                                <span className="font-bold">{t("agenda.pub_label")}</span> {fmtDate(popup.publication_date)}
                                                {days >= 0 ? ` ${t("agenda.in_days", { count: days, unit: days === 1 ? t("agenda.unit_day") : t("agenda.unit_days") })}` : ` ${t("agenda.published")}`}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>

                            {popup.items?.length > 0 && (
                                <div className="mb-5">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("agenda.booked")}</div>
                                    <div className="space-y-1">
                                        {popup.items.map((it, i) => {
                                            const p = products.find((x) => x.id === it.product_id);
                                            if (!p) return null;
                                            return (
                                                <div key={i} className="text-sm">
                                                    <span className="text-zinc-950 font-medium">{p.name}</span>
                                                    <span className="text-zinc-500"> — {p.tagline}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Scheduled vs unscheduled */}
                            {popup.date ? (
                                <div className="bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3 mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("agenda.legend_scheduled")}</div>
                                        {canEdit && (
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(t("agenda.unschedule_confirm"))) return;
                                                    const ok = await reschedule(popup.request_id, "", "");
                                                    if (ok) { toast.success(t("agenda.unschedule_done")); setPopup(null); load(); }
                                                }}
                                                className="text-[10px] text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                                                data-testid="popup-unschedule-btn"
                                            >
                                                <X className="h-3 w-3" /> {t("agenda.unschedule")}
                                            </button>
                                        )}
                                    </div>
                                    {canEdit ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="date"
                                                value={popup._editDate ?? popup.date}
                                                onChange={(e) => setPopup({ ...popup, _editDate: e.target.value })}
                                                className="bg-[#FFFFFF] border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                                                data-testid="popup-edit-date"
                                            />
                                            <input
                                                type="time"
                                                step="900"
                                                min="07:00"
                                                max="19:00"
                                                value={scheduleTime}
                                                onChange={(e) => setScheduleTime(e.target.value)}
                                                className="bg-[#FFFFFF] border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D] [color-scheme:dark]"
                                                data-testid="popup-edit-time"
                                            />
                                            {((popup._editDate && popup._editDate !== popup.date) || scheduleTime !== (popup.time || "10:00")) && (
                                                <Button
                                                    onClick={async () => {
                                                        const ok = await reschedule(popup.request_id, popup._editDate || popup.date, scheduleTime);
                                                        if (ok) { toast.success(t("agenda.saved")); setPopup(null); load(); }
                                                    }}
                                                    className="h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs rounded-sm"
                                                    data-testid="popup-save-edit-btn"
                                                >
                                                    {t("agenda.save")}
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-2 text-zinc-950">
                                                {fmtDate(popup.date)}
                                                <Clock className="h-3 w-3 text-[#E5A93D]" />
                                                {popup.time || "—"}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    // pre-fill new preferred slots with empty entries
                                                    setCancelData({
                                                        reason: "",
                                                        slots: [
                                                            { date: "", time: "10:00" },
                                                            { date: "", time: "10:00" },
                                                            { date: "", time: "10:00" },
                                                        ],
                                                    });
                                                    setCancelOpen(true);
                                                }}
                                                className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 active:scale-95"
                                                data-testid="agent-cancel-shoot-btn"
                                            >
                                                <X className="h-3 w-3" /> {t("agenda.cancel_btn")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : canEdit && (
                                <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/30 rounded-sm p-3 mb-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#7C3AED] mb-2">{t("agenda.plan_in")}</div>
                                    {(popup.preferred_date || popup.preferred_slots?.length > 0) && (
                                        <div className="space-y-1.5 mb-3">
                                            <div className="text-[10px] text-zinc-500">{t("agenda.pref_moments")}</div>
                                            {[{ date: popup.preferred_date, time: popup.preferred_time }, ...(popup.preferred_slots || [])].filter((s) => s?.date).map((s, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => { setPopup({ ...popup, _scheduleDate: s.date }); setScheduleTime(s.time || "10:00"); }}
                                                    className={`w-full text-left text-xs px-2.5 py-1.5 rounded-sm border transition ${popup._scheduleDate === s.date ? "bg-[#E5A93D]/15 border-[#E5A93D] text-zinc-950" : "bg-[#F4F4F5] border-zinc-200 hover:border-[#E5A93D]/50 text-zinc-600"}`}
                                                    data-testid={`pref-pick-${i}`}
                                                >
                                                    {t("agenda.pref_n", { n: i + 1, date: fmtDate(s.date), time: s.time })}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="time"
                                            step="900"
                                            min="07:00"
                                            max="19:00"
                                            value={scheduleTime}
                                            onChange={(e) => setScheduleTime(e.target.value)}
                                            className="bg-[#F4F4F5] border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D] [color-scheme:dark]"
                                            data-testid="popup-time"
                                        />
                                        <Button onClick={scheduleFromPopup} disabled={!popup._scheduleDate && !popup.preferred_date} className="flex-1 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs rounded-sm" data-testid="popup-schedule-btn">
                                            {t("agenda.plan_on", { date: fmtDate(popup._scheduleDate || popup.preferred_date) || "—" })}
                                        </Button>
                                    </div>
                                    <p className="text-[11px] text-zinc-500 mt-2 italic">{t("agenda.or_drag")}</p>
                                </div>
                            )}

                            <Button onClick={() => navigate(projectLink(popup.request_id))} variant="ghost" className="w-full h-10 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm">
                                {t("agenda.open_project")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Agent cancellation modal — propose 3 new dates */}
            {cancelOpen && popup && (
                <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => !cancelling && setCancelOpen(false)} data-testid="cancel-modal">
                    <div className="bg-[#FFFFFF] border border-red-500/40 rounded-md max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <X className="h-5 w-5 text-red-600" />
                                <h3 className="font-display font-bold text-xl text-white">{t("agenda.cancel_btn")}</h3>
                            </div>
                            <p className="text-sm text-zinc-500 mb-2">
                                {t("agenda.c_intro")} <span className="text-zinc-950 font-medium">{fmtDate(popup.date)}</span>
                                {popup.time && <> {t("agenda.at")} <span className="text-zinc-950 font-medium">{popup.time}</span></>} {t("agenda.c_for")}
                            </p>
                            <div className="text-sm text-[#E5A93D] mb-5">{popup.address_full || popup.address}</div>

                            <div className="mb-5">
                                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{t("agenda.c_reason")}</label>
                                <textarea
                                    value={cancelData.reason}
                                    onChange={(e) => setCancelData({ ...cancelData, reason: e.target.value })}
                                    placeholder={t("agenda.c_reason_ph")}
                                    rows={2}
                                    className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm px-3 py-2 text-sm text-zinc-950 focus:outline-none focus:border-red-500/50"
                                    data-testid="cancel-reason"
                                />
                            </div>

                            <div className="mb-5">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("agenda.c_slots")}</div>
                                <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
                                    {t("agenda.c_slots_hint")}
                                </p>
                                <div className="space-y-2">
                                    {cancelData.slots.map((s, i) => (
                                        <div key={i} className="flex items-center gap-2" data-testid={`cancel-slot-${i}`}>
                                            <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold bg-[#E5A93D]/15 text-[#E5A93D] border border-[#E5A93D]/30">{i + 1}</span>
                                            <input
                                                type="date"
                                                value={s.date}
                                                onChange={(e) => {
                                                    const next = [...cancelData.slots];
                                                    next[i] = { ...next[i], date: e.target.value };
                                                    setCancelData({ ...cancelData, slots: next });
                                                }}
                                                className="flex-1 bg-[#F4F4F5] border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                                                data-testid={`cancel-slot-date-${i}`}
                                            />
                                            <input
                                                type="time"
                                                value={s.time}
                                                onChange={(e) => {
                                                    const next = [...cancelData.slots];
                                                    next[i] = { ...next[i], time: e.target.value };
                                                    setCancelData({ ...cancelData, slots: next });
                                                }}
                                                className="w-24 bg-[#F4F4F5] border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                                                data-testid={`cancel-slot-time-${i}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    onClick={() => setCancelOpen(false)}
                                    disabled={cancelling}
                                    variant="ghost"
                                    className="flex-1 h-11 border border-zinc-200 text-zinc-600 rounded-sm"
                                    data-testid="cancel-modal-cancel"
                                >
                                    {t("agenda.c_close")}
                                </Button>
                                <Button
                                    onClick={async () => {
                                        // Validate at least one date
                                        const validSlots = cancelData.slots.filter((s) => s.date);
                                        if (validSlots.length === 0) {
                                            toast.error(t("agenda.c_need_one"));
                                            return;
                                        }
                                        setCancelling(true);
                                        try {
                                            // First slot becomes the primary preferred_date/time, rest go to preferred_slots
                                            const [first, ...rest] = validSlots;
                                            await api.patch(`/requests/${popup.request_id}/schedule`, {
                                                scheduled_date: "",
                                                scheduled_time: "",
                                                preferred_date: first.date,
                                                preferred_time: first.time,
                                                preferred_slots: rest,
                                                cancel_reason: cancelData.reason || undefined,
                                            });
                                            toast.success(t("agenda.c_done"));
                                            setCancelOpen(false);
                                            setPopup(null);
                                            load();
                                        } catch (err) {
                                            toast.error(err.response?.data?.detail || t("agenda.failed"));
                                        }
                                        setCancelling(false);
                                    }}
                                    disabled={cancelling}
                                    className="flex-1 h-11 bg-red-500 hover:bg-red-400 text-zinc-950 font-bold rounded-sm"
                                    data-testid="cancel-modal-submit"
                                >
                                    {cancelling ? t("agenda.c_busy") : t("agenda.c_submit")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
